import {
  defaultLocale,
  isDefaultLocale,
  supportedLocales,
  type LocaleCode,
} from '../../config/locales';

/**
 * Prefix an internal navigation URL with the current locale so visitors who
 * chose a non-default locale stay in that locale across in-site clicks (logo,
 * header nav items, mobile menu). External URLs, empty strings, hash links,
 * and URLs already starting with a supported locale segment are returned
 * unchanged.
 *
 * The helper exists because Storyblok's nav-item URLs are authored as locale-
 * neutral paths (`/about`, `/members`, etc.). Persistence (the inline `<head>`
 * redirect) only fires on `/`, so without rewriting the rendered `href` a
 * Spanish-locale visitor on `/es/about` would click "Members" and SPA-navigate
 * to `/members` (English) — the persistence layer cannot help on non-root URLs
 * by design.
 *
 * - `href` is anything one would put in `<a href>` or `<Link to>`.
 * - `locale` is the current page locale, typically `pageContext.lang` propagated
 *   through `SiteShell` → `Header`.
 */
export function localizeHref(href: string, locale: LocaleCode): string {
  if (!href) return href;
  if (!href.startsWith('/')) return href; // external URLs, mailto:, tel:, #anchor — untouched
  if (isDefaultLocale(locale)) return href; // default locale uses unprefixed URLs

  // Already locale-prefixed: respect the link author's explicit locale intent.
  const firstSegment = href.split('/')[1] ?? '';
  if ((supportedLocales as readonly string[]).includes(firstSegment)) return href;

  // Special-case the locale-neutral root.
  if (href === '/') return `/${locale}/`;

  return `/${locale}${href}`;
}

// Re-export defaultLocale + supportedLocales for callers that want to spot-check
// the helper's behavior (mostly tests). Not part of the runtime contract.
export { defaultLocale, supportedLocales };
